/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { SearchResultRepositoryPartial } from '@atlassian/dynamic-cards-search-client/graphql/third-party-graph-entity';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { removeMarkdown } from '../../../../../../../common/utils/remove-markdown';
import {
	enrichExcerpt,
	getIntegrationKey,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useBootstrap, usePrimaryProduct } from '../../../../../../../controllers/store';
import { messages } from './messages';

const GITHUB_INTEGRATION_KEY = 'github';

interface RepositorySearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultRepositoryPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const RepositorySearchResult = ({
	partial,
	position,
	icon,
}: RepositorySearchResultProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { lastModifiedDate, url, permissionLevel, title, excerpt } = partial;
	const integrationKey = getIntegrationKey(partial.integrationId);

	const label = formatMessage(messages.repositoryLabel);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getDescription = (excerpt?: string) => {
		const markdownRemoved = removeMarkdown(excerpt);
		return processExcerpt(markdownRemoved, true);
	};

	const lastModifiedComponent =
		lastModifiedDate && integrationKey !== GITHUB_INTEGRATION_KEY ? (
			<LastModified lastModified={lastModifiedDate} />
		) : null;

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={getDescription(excerpt)}
			headerMetadata={
				<SearchMetadata
					product={integrationKey}
					productSelected={true}
					parts={[label, ['padlock', padlockIcon], ['last-modified', lastModifiedComponent]]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
